import React from 'react';
import Layout from '../../components/Layout';
import ViewListing from './ViewListing';
import NotFound from '../notFound/NotFound';
import fetch from '../../core/fetch';
import { url, fileuploadDir } from '../../config.js';
import moment from 'moment';
import { checkAvailability } from '../../actions/checkAvailability';
import { setPersonalizedValues } from '../../actions/personalized';

const title = 'View Listing';

function renderNotFound() {
  return {
    title,
    component: <Layout><NotFound title={title} /></Layout>,
    status: 404,
  };
}

export default async function action({ store, params, query }) {


  let listTitle, listDescription, listPhoto, lat, lng, startDate, endDate;
  let startTime, endTime;
  let productSchema = null, breadcrumbSchema = null;
  const baseCurrency = store.getState().currency.base;
  const isAdmin = store.getState().runtime.isAdminAuthenticated;

  const getListquery = `
      query GetListMeta($listId: Int!) {
        getListMeta(listId: $listId) {
          id
          title
          description
          isPublished
          coverPhoto
          listPhotos {
            id
            name
          }
          status
          lat
          lng
          city
          state
          country
          reviewsCount
          reviewsStarRating
          user {
            profile {
              firstName
            }
          }
          listingData {
            maxDay
            minDay
            basePrice
            currency
          }
        }
      }
    `;

  // From URI
  let listURL = params.listId, listId, listURLData, preview = false, URLRoomType = false, maximumNights = 0, currentDay, minimumNights = 0;

  if (params.preview) preview = true;

  if (listURL && listURL.indexOf('-') >= 0) {
    listURLData = listURL.split('-');
    listId = listURLData[listURLData.length - 1];
  } else {
    listId = listURL;
  }

  if (listId === undefined || isNaN(listId)) {
    renderNotFound();
    return;
  }

  // const dates = params.dates;
  const resp = await fetch('/graphql', {
    method: 'post',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      query: getListquery,
      variables: { listId }
    }),
  });
  const { data } = await resp.json();

  currentDay = moment().format('YYYY-MM-DD')

  if ('startdate' in query && 'enddate' in query) {
    startDate = moment(query.startdate).format("YYYY-MM-DD");
    endDate = moment(query.enddate).format("YYYY-MM-DD");
    if (currentDay > startDate || currentDay > endDate || startDate > endDate || startDate == 'Invalid date' || endDate == 'Invalid date') {
      renderNotFound();
      return;
    }
    store.dispatch(setPersonalizedValues({ name: 'startDate', value: startDate }));
    store.dispatch(setPersonalizedValues({ name: 'endDate', value: endDate }));
  } else if ('startdate' in query || 'enddate' in query) {
    renderNotFound();
    return;
  }

  if ('address' in query) {
    store.dispatch(setPersonalizedValues({ name: 'location', value: query.address }));
  }

  if ('startTime' in query && 'endTime' in query) {
    startTime = query.startTime
    endTime = query.endTime;
  }

  if ('roomType' in query) URLRoomType = query.roomType;

  if (data && data.getListMeta) {
    if (!data.getListMeta.isPublished && !preview && !isAdmin) {
      renderNotFound();
      return;
    }
    
    // Track views/clicks (only on client side, not SSR)
    if (typeof window !== 'undefined') {
      // Track for featured listings (FeaturedAnalytics)
      fetch('/graphql', {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: `mutation { trackFeaturedClick(listId: ${listId}) { status } }`
        }),
      }).catch(() => {});
      
      // Track for non-featured listings (ListViews) - endpoint skips if featured
      fetch('/track-view', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listId })
      }).catch(() => {});
    }
    
    listTitle = data.getListMeta.title;
    listDescription = data.getListMeta.description;
    lat = data.getListMeta.lat;
    lng = data.getListMeta.lng;
    maximumNights = data && data.getListMeta && data.getListMeta.listingData && data.getListMeta.listingData.maxDay ? data.getListMeta.listingData.maxDay : 0;
    minimumNights = data && data.getListMeta && data.getListMeta.listingData && data.getListMeta.listingData.minDay ? data.getListMeta.listingData.minDay : 0;
    if (data?.getListMeta?.listPhotos?.length > 0) {
      let coverImage = data?.getListMeta?.listPhotos?.find(o => o?.id == data?.getListMeta?.coverPhoto)
      listPhoto = url + fileuploadDir.replace('.', "") + (coverImage ? coverImage?.name : data?.getListMeta?.listPhotos[0]?.name);
    }
    if (startDate && endDate && startTime && endTime) await store.dispatch(checkAvailability(listId, startDate, endDate, maximumNights, startTime, endTime, minimumNights));
    
    // SEO: Generate JSON-LD schemas for SSR (guaranteed to be in initial HTML)
    const listing = data.getListMeta;
    const reviewsCount = listing.reviewsCount || 0;
    const reviewsStarRating = listing.reviewsStarRating || 0;
    const starRatingValue = reviewsCount > 0 && reviewsStarRating > 0 
      ? Math.round(reviewsStarRating / reviewsCount) 
      : 0;
    const basePrice = listing.listingData?.basePrice || 0;
    const currency = listing.listingData?.currency || 'USD';
    
    productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": listing.title,
      "description": listing.description ? listing.description.substring(0, 200) : '',
      "image": listing.listPhotos && listing.listPhotos.length > 0 
        ? listing.listPhotos.slice(0, 5).map(photo => `${url}${fileuploadDir.replace('.', '')}x_large_${photo.name}`)
        : [],
      "brand": { "@type": "Brand", "name": "Navega" },
      "offers": {
        "@type": "Offer",
        "url": `${url}/boats/${listId}`,
        "priceCurrency": currency,
        "price": basePrice,
        "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Person",
          "name": listing.user?.profile?.firstName || "Host"
        }
      },
      ...(reviewsCount > 0 && starRatingValue > 0 ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": starRatingValue,
          "reviewCount": reviewsCount,
          "bestRating": "5",
          "worstRating": "1"
        }
      } : {})
    };

    breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Inicio", "item": url },
        { "@type": "ListItem", "position": 2, "name": listing.city || "Botes", "item": `${url}/s?address=${encodeURIComponent(listing.city || '')}` },
        { "@type": "ListItem", "position": 3, "name": listing.title, "item": `${url}/boats/${listId}` }
      ]
    };

  } else {
    renderNotFound();
    return;
  }

  let viewListingBottom = 'viewListingBottom';

  return {
    title: listTitle || title,
    description: listDescription || '',
    image: listPhoto || '',
    jsonLd: [productSchema, breadcrumbSchema],
    component: <Layout viewListingBottom={viewListingBottom}><ViewListing
      title={title}
      preview={preview}
      lat={lat}
      lng={lng}
      listId={Number(listId)}
      startDate={startDate}
      endDate={endDate}
      baseCurrency={baseCurrency}
      URLRoomType={URLRoomType}
      startTime={startTime}
      endTime={endTime}
    /></Layout>,
  };
};
